<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { applyCommunityBranding, clearCommunityBranding } from '@/branding/runtime';
import HomepageItemPrivateNoAccessDialog from '@/components/dialogs/HomepageItemPrivateNoAccessDialog.vue';
import { constant } from '@/constant';
import PageLoading from '@/views/PageLoading.vue';
import { usePage } from '@/composables/usePage';
import { authService } from '@/services/auth.service';
import { type HomepageItemNoAccessPayload } from '@/composables/useHomepageItemNavigation';
import { useCommunityStore } from '@/store/communityStore';
import { useConfigurationStore } from '@/store/configurationStore';
import { useUserStore } from '@/store/userStore';
import type { HomepageItem } from '@/store/homepageItem.interface';
import { themeRegistry } from '@/themes/registry';
import type { BlockType } from '@/themes/types';
import { useDrawnBlocks } from '@/themes/useDrawnBlocks';
import { getLabel } from '@/utils/data-display-utils';
import { communityDocumentTitle, pageDocumentTitle, pageHeading } from '@/utils/page-heading';

const props = withDefaults(
  defineProps<{
    pageId?: number | null;
    slug?: string | null;
    isHomepage?: boolean;
  }>(),
  {
    pageId: null,
    slug: null,
    isHomepage: false,
  },
);

const route = useRoute();
const router = useRouter();
const communityStore = useCommunityStore();
const configurationStore = useConfigurationStore();
const userStore = useUserStore();

const resolvedPageId = computed(() => props.pageId ?? null);
const resolvedSlug = computed(() => props.slug ?? (route.params.slug as string | undefined) ?? null);
// The admin's Preview opens a draft with `preview=draft`, which the API answers only for an administrator of the
// community, signed in on the site with the site's own token.
const preview = computed(() => route.query.preview === 'draft');

const { page, blocks, themeKey, layoutKey, isLocaleSupported, isLoading, currentLocale, loadPage } = usePage(
  resolvedPageId,
  resolvedSlug,
  preview,
);

const layoutComponent = computed(() =>
  defineAsyncComponent(themeRegistry.getLayout('page', themeKey.value, layoutKey.value)),
);
const designSystem = computed(() => themeRegistry.getDesignSystem('page', themeKey.value));
// The page's one h1: its title, unless a block the layout draws draws it already.
const { drawnBlocks } = useDrawnBlocks(
  'page',
  () => themeKey.value,
  () => blocks.value,
  (block) => block.pageBlockType as BlockType,
);
const heading = computed(() => (page.value ? pageHeading(page.value, drawnBlocks.value) : null));
// Only an administrator's preview reads a draft: the banner tells them visitors do not see it.
const isDraft = computed(() => page.value?.status === constant.Page.Status.Draft);
const homepageItemPrivateNoAccessDialogRef = ref<InstanceType<typeof HomepageItemPrivateNoAccessDialog>>();
const selectedItemNeedAuth = ref<HomepageItem | null>(null);
const needLogin = ref(false);

function openNoAccessDialog(item: HomepageItem, loginRequired = false) {
  selectedItemNeedAuth.value = item;
  needLogin.value = loginRequired;
  homepageItemPrivateNoAccessDialogRef.value?.openDialog();
}

function handleNoAccess(payload: HomepageItemNoAccessPayload) {
  openNoAccessDialog(payload.item, payload.needLogin);
}

// The preview is a reason to read again too: the same page, asked as a draft or no longer, is not the page on screen.
watch(
  [resolvedPageId, resolvedSlug, preview],
  async () => {
    await loadPage();
  },
  { immediate: true },
);

// A preview link reaches a published page as any visitor does, signed in or not. Only a draft needs a sign-in on the
// site, its own token: the API answers it to an administrator alone. So a preview that finds no page asks whoever is
// signed out to sign in, from the site's own user, which the router has loaded before any page, and comes back to it.
// A signed-in participant gets the not-found page.
watch(
  [() => isLoading.value, () => page.value],
  async ([loading, currentPage]) => {
    if (loading || currentPage) return;

    if (preview.value && !userStore.user?.id) {
      await authService.login({
        page: props.isHomepage
          ? { homepage: true, hash: route.hash }
          : { slug: resolvedSlug.value ?? undefined, hash: route.hash },
      });
      return;
    }
    await router.replace({ name: 'not-found' });
  },
  { immediate: true },
);

// The tab names the page in the visitor's language, and follows a switch of it, which reloads nothing.
const documentTitle = computed(() =>
  page.value
    ? pageDocumentTitle(page.value, communityStore.community, communityStore.community?.options?.pages?.homepage_id)
    : null,
);
watch(
  documentTitle,
  (title) => {
    if (title) document.title = title;
  },
  { immediate: true },
);

watch(
  [() => isLoading.value, () => route.query.projectId, () => communityStore.isLoaded],
  ([loading, projectId, communityLoaded]) => {
    if (loading || !projectId || !communityLoaded) return;

    const parsedProjectId = parseInt(projectId as string, 10);
    if (Number.isNaN(parsedProjectId)) return;

    const project = communityStore.getProject(parsedProjectId);
    if (!project) return;

    const result = communityStore.isUserAllowedAccessProject(project, userStore.user);
    if (!result.allowed) {
      openNoAccessDialog(project, result.reason === 'login');
    }
  },
  { immediate: true },
);

watch(
  [() => designSystem.value, () => communityStore.community, () => configurationStore.brandingPresets],
  async ([currentDesignSystem, currentCommunity]) => {
    if (currentDesignSystem !== 'modern') {
      clearCommunityBranding();
      return;
    }

    await applyCommunityBranding(currentCommunity);
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  clearCommunityBranding();
  // The views a visitor goes on to set no title of their own: they get the community's back, not this page's.
  document.title = communityDocumentTitle(communityStore.community) ?? document.title;
});
</script>

<template>
  <div v-if="!isLoading" class="page-view" :class="`page-view--${designSystem}`" :data-design-system="designSystem">
    <div v-if="isDraft" class="bg-warning-bg px-4 py-2 text-center text-small text-warning" role="status">
      {{ getLabel('page.draft', page?.languageEnabled) }}
    </div>
    <component
      v-if="page"
      :is="layoutComponent"
      :page="page"
      :blocks="blocks"
      :community="communityStore.community"
      :theme-key="themeKey"
      :heading="heading"
      :is-locale-supported="isLocaleSupported"
      :current-locale="currentLocale"
      @no-access="handleNoAccess"
    />
    <HomepageItemPrivateNoAccessDialog
      v-if="page"
      ref="homepageItemPrivateNoAccessDialogRef"
      :home-link="!props.isHomepage"
      :item="selectedItemNeedAuth"
      :need-login="needLogin"
      :language-enabled="communityStore.community?.languageEnabled"
    />
  </div>
  <PageLoading v-else :language-enabled="communityStore.community?.languageEnabled" />
</template>

<style scoped>
.page-view {
  display: flex;
  flex: 1 1 auto;
  min-height: 100%;
  flex-direction: column;
}
</style>
